import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/lib/apiClient";
import { PestRiskPanel } from "@/components/PestRiskPanel";
import { RainBanner } from "@/components/RainAlerts";
import { Button } from "@/components/ui/button";
import { X, Move, Droplets, CloudRain, Loader2, Sprout, Wheat, CalendarDays, ExternalLink } from "lucide-react";

const FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const norm = (a) => (Array.isArray(a) ? a.map(Number).filter((m) => m >= 1 && m <= 12) : []);

export function FieldPinPanel({ field, rain, onClose, onMove }) {
  const navigate = useNavigate();
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setWeather(null);
    apiClient.get("/weather/point", { params: { lat: field.lat, lon: field.lon } })
      .then((r) => setWeather(r.data)).catch(() => setWeather(null)).finally(() => setLoading(false));
  }, [field.id, field.lat, field.lon]);

  const month = new Date().getMonth() + 1;
  const crops = field.analysis?.crops || [];
  const sow = crops.filter((c) => norm(c.calendar?.sow_months).includes(month)).map((c) => c.name);
  const harvest = crops.filter((c) => norm(c.calendar?.harvest_months).includes(month)).map((c) => c.name);
  const score = Math.round(field.analysis?.soil_health?.score || 0);

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="pin-panel">
      <div className="bg-[#143D29] text-emerald-50 p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-emerald-200/70 font-semibold">{field.location || `${field.lat.toFixed(3)}, ${field.lon.toFixed(3)}`}</div>
            <h3 className="font-display text-xl font-bold truncate" data-testid="pin-field-name">{field.name}</h3>
            <div className="text-sm text-emerald-100/80 font-mono-tech mt-1">pH {field.soil?.ph} · {field.soil?.soil_type} · Score {score}</div>
          </div>
          <button data-testid="pin-close-btn" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="p-5 space-y-5">
        <RainBanner rain={rain} />
        <div data-testid="pin-weather">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Live weather here</div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" /> Fetching forecast…</div>
          ) : weather ? (
            <>
              <div className="flex items-center justify-between bg-[#EAF6EF] rounded-xl p-4">
                <div>
                  <div className="font-mono-tech text-3xl font-bold text-emerald-950">{Math.round(weather.current?.temp_c)}°</div>
                  <div className="text-sm text-stone-600">{weather.current?.condition}</div>
                </div>
                <div className="text-right text-sm text-stone-600">
                  <div className="flex items-center justify-end gap-1"><Droplets className="h-4 w-4 text-blue-500" /> {weather.current?.humidity}% humidity</div>
                  <div className="mt-1">Topsoil: <b className="text-emerald-900">{weather.current?.soil_moisture}</b></div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {weather.forecast?.map((d, i) => (
                  <div key={i} className="rounded-lg border border-stone-200 p-2 text-center">
                    <div className="text-xs font-semibold text-emerald-950">{d.day}</div>
                    <div className="font-mono-tech text-sm text-stone-700">{Math.round(d.high_c)}°/{Math.round(d.low_c)}°</div>
                    <div className="text-[11px] text-blue-600 flex items-center justify-center gap-0.5"><CloudRain className="h-3 w-3" />{d.rain_mm}mm</div>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-sm text-stone-500">Weather unavailable right now.</p>}
        </div>

        <div data-testid="pin-reminders">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2 flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {FULL[month - 1]} on this field</div>
          {sow.length || harvest.length ? (
            <div className="space-y-1.5 text-sm">
              {sow.length > 0 && <div className="flex items-start gap-1.5 text-stone-700"><Sprout className="h-4 w-4 text-[#D97706] shrink-0 mt-0.5" /><span><b className="text-[#D97706]">Sow:</b> {sow.join(", ")}</span></div>}
              {harvest.length > 0 && <div className="flex items-start gap-1.5 text-stone-700"><Wheat className="h-4 w-4 text-[#2D6A4F] shrink-0 mt-0.5" /><span><b className="text-[#2D6A4F]">Harvest:</b> {harvest.join(", ")}</span></div>}
            </div>
          ) : <p className="text-sm text-stone-500">Nothing to sow or harvest this month.</p>}
        </div>

        <PestRiskPanel field={field} />

        <div className="flex gap-2 pt-1">
          <Button data-testid="pin-move-btn" onClick={onMove} variant="outline" size="sm" className="border-stone-300 flex-1"><Move className="h-3.5 w-3.5 mr-1" /> Move pin</Button>
          <Button data-testid="pin-open-field-btn" onClick={() => navigate("/app/fields")} size="sm" className="bg-[#2D6A4F] hover:bg-[#143D29] text-white flex-1"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Open field</Button>
        </div>
      </div>
    </div>
  );
}
